import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { savedRepliesApi } from '../../api/dashboard.api.js';
import { usePermission } from '../../hooks/usePermission.js';
import { IconNote, IconSearch } from '../icons/Icons.jsx';

let cache = null;
export function invalidateSavedReplies() {
  cache = null;
}

// Popover listing saved replies; picking one hands the raw template back to the caller.
export function SavedRepliesPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState(cache || []);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const { isAdmin } = usePermission();

  useEffect(() => {
    if (!open) return undefined;
    if (!cache) {
      savedRepliesApi.list().then(({ data }) => { cache = data; setReplies(data); }).catch(() => {});
    }
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const visible = replies.filter((r) => !q || r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q));

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-[8px] px-2 py-1.5 hover:bg-[var(--neutralSoft)]" style={{ color: 'var(--textMuted)' }} aria-expanded={open}>
        <IconNote size={14} /> Saved replies
      </button>
      {open && (
        <div className="absolute left-0 bottom-full mb-2 z-40 w-[min(92vw,340px)] rounded-[12px] overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: 'var(--shadowFloat)' }}>
          <div className="relative p-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <IconSearch size={14} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--textDim)' }} />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a saved reply" className="input !min-h-[36px] pl-8 text-sm" aria-label="Find a saved reply" />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {visible.length === 0 && (
              <p className="px-3 py-5 text-center text-sm" style={{ color: 'var(--textMuted)' }}>
                {replies.length ? 'No match.' : 'No saved replies yet.'}
              </p>
            )}
            {visible.map((reply) => (
              <button key={reply.id} type="button" onClick={() => { onPick(reply.body); setOpen(false); setQuery(''); }} className="w-full text-left rounded-[9px] px-3 py-2 hover:bg-[var(--bg)]">
                <span className="block text-sm font-semibold" style={{ color: 'var(--text)' }}>{reply.title}</span>
                <span className="block text-xs line-clamp-2" style={{ color: 'var(--textMuted)' }}>{reply.body}</span>
              </button>
            ))}
          </div>
          {isAdmin && (
            <Link to="/settings?section=replies" className="block px-3 py-2 text-xs font-semibold" style={{ borderTop: '1px solid var(--border)', color: 'var(--accentHot)' }}>
              Manage saved replies
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
